// Dashboard table helpers: FK cell name resolution, column ordering (base
// order-by-`order` + user manage_columns override), and the field/value
// resolution that mirrors project.js `convert_to_sort_arr` + `get_name_by_id`.
//
// Faithful re-platform of the Backbone whiteboard. See §3.E / §4 / §10 of
// PHASE2_DASHBOARD_INVENTORY.md.

import type {
  IDashboardColumn,
  IDashboardProject,
  ICompany,
  IContact,
  IMfgUser,
  IMarketType,
  ColumnRelModel,
} from 'models';

/** id → entity maps for FK lookups (one per rel_model collection). */
export interface LookupMaps {
  companies: Map<number, ICompany>; // rel_model='company'  (/projects/companies/)
  contacts: Map<number, IContact>; // rel_model='company_contact' (/projects/contacts/)
  partners: Map<number, ICompany>; // rep_company VALUE (/cms/.../mfg/partners/)
  mfgUsers: Map<number, IMfgUser>; // rep_user value + rep_company GUARD (/mfg/1/users/)
  marketTypes: Map<number, IMarketType>; // rel_model='market_type'
}

/** Build an id→entity Map from a lookup array. */
export function toMap<T extends { id: number }>(arr: T[] | undefined): Map<number, T> {
  const m = new Map<number, T>();
  (arr ?? []).forEach((e) => m.set(Number(e.id), e));
  return m;
}

/**
 * Resolve an FK display name by id + rel_model — mirrors project.js
 * `get_name_by_id`.
 *
 * DEVIATION (§10 item 15): the Backbone `rep_company` case GUARDS on the
 * mfg-users collection but READS `.name` from the partners collection — a
 * latent crash (TypeError aborting the whole row when an id is in users but
 * not partners) plus a silent-blank (id in partners but not users). We FIX it
 * here: guard AND read the SAME collection (partners). This is a code-
 * correctness fix only; dev3 has ZERO rep_company columns so the visual diff is
 * nil either way. All other cases keep guard==value (faithful).
 */
export function resolveFkName(
  rawId: unknown,
  relModel: ColumnRelModel,
  maps: LookupMaps
): string {
  const id = rawId == null || rawId === '' ? null : Number(rawId);
  if (id == null || Number.isNaN(id)) return '';
  switch (relModel) {
    case 'company':
      return maps.companies.get(id)?.name ?? '';
    case 'company_contact':
      return maps.contacts.get(id)?.name ?? '';
    case 'rep_company': {
      // DEVIATION: guard+read SAME collection (partners) — see docstring.
      const partner = maps.partners.get(id);
      return partner ? partner.name ?? '' : '';
    }
    case 'rep_user':
      return maps.mfgUsers.get(id)?.first_name ?? '';
    case 'market_type':
      return maps.marketTypes.get(id)?.name ?? '';
    default:
      return '';
  }
}

/** A resolved body cell: name attribute (bare key) + display value. */
export interface ResolvedCell {
  /** li[name] attribute — the bare obj key (no `_id`), mirrors project.js a.name. */
  name: string;
  /** display string for the <span>. */
  value: string;
}

/**
 * Build the ordered list of body cells for one project row, honoring column
 * order + visibility — mirrors project.js `convert_to_sort_arr`.
 *
 * For each visible column (in the given order) we find the matching project key
 * by `field_name === key` OR `field_name === key + '_id'`. FK columns resolve
 * via `resolveFkName`; non-FK columns read the raw value. `create_date` is
 * special-cased to the `created_on_date` key (value falls back to `create_date`).
 */
export function buildRowCells(
  project: IDashboardProject,
  orderedVisibleColumns: IDashboardColumn[],
  maps: LookupMaps
): ResolvedCell[] {
  const cells: ResolvedCell[] = [];
  const obj = project as Record<string, unknown>;

  for (const col of orderedVisibleColumns) {
    const fieldName = col.field_name;
    // Find the project key that this column maps to.
    let matchedKey: string | null = null;
    let valueKey: string | null = null; // the key to read raw value from

    for (const rawKey of Object.keys(obj)) {
      // create_date is displayed/sorted under created_on_date (project.js);
      // HeaderView remaps the date column's field_name to `create_date`, so the
      // column may arrive as EITHER `created_on_date` or `create_date`. Match
      // against the remapped key, the raw key, and the `_id` FK form.
      const key = rawKey === 'create_date' ? 'created_on_date' : rawKey;
      if (
        fieldName === key ||
        fieldName === rawKey ||
        fieldName === `${key}_id` ||
        fieldName === `${rawKey}_id`
      ) {
        matchedKey = key;
        valueKey = rawKey; // read from the ORIGINAL key (create_date, not created_on_date)
        break;
      }
    }

    if (matchedKey == null) {
      // Columns with no serialized project key (e.g. price_net_actual_id /
      // "Project Total Net Price") still render an EMPTY trailing cell in OLD
      // (28 headers ⇒ 28 cells per row) — keep header/body cell counts equal.
      cells.push({ name: '', value: '' });
      continue;
    }

    let value: string;
    if (col.rel_model) {
      // FK: the id lives at the bare key (e.g. customer_co_id) on the project.
      const idKey = matchedKey.endsWith('_id') ? matchedKey : `${matchedKey}_id`;
      const rawId = obj[idKey] ?? obj[matchedKey];
      value = resolveFkName(rawId, col.rel_model, maps);
    } else {
      const raw = valueKey != null ? obj[valueKey] : undefined;
      value = raw == null ? '' : String(raw);
    }
    cells.push({ name: matchedKey, value });
  }
  return cells;
}

/**
 * Compute the ordered, visible columns for the dashboard table.
 *
 * Two ordering inputs combined (§3.E "base order vs user override"):
 *  (a) BASE order — sort all columns ascending by integer `order` (stable);
 *  (b) USER OVERRIDE — if `manageColumns` (user.manage_columns ids in order) is
 *      non-empty, the visible set + their left-to-right order come from it.
 *
 * The fixed `project_name` column is always excluded here (it is the static
 * frozen first column). If `manageColumns` is empty, all visibility===true
 * columns show in base order.
 */
export function orderedVisibleColumns(
  columns: IDashboardColumn[] | undefined,
  manageColumns: number[]
): IDashboardColumn[] {
  if (!columns || columns.length === 0) return [];

  // HeaderView remaps the date column's field_name `created_on_date` → `create_date`
  // (header.js) so its sort/search/filter key matches the ProjectFilter
  // `create_date` key. The column LABEL stays "Created on" (parity 51).
  const remapped = columns.map((c) =>
    c.field_name === 'created_on_date' ? { ...c, field_name: 'create_date' } : c
  );

  // (a) base: stable ascending by `order`.
  const base = [...remapped].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  // Exclude the fixed name column from the dynamic set.
  const dynamic = base.filter((c) => c.value !== 'project_name');

  if (manageColumns && manageColumns.length > 0) {
    // (b) user override: keep only ids in manage_columns, in that exact order.
    const byId = new Map<number, IDashboardColumn>();
    dynamic.forEach((c) => byId.set(Number(c.id), c));
    const result: IDashboardColumn[] = [];
    for (const id of manageColumns) {
      const c = byId.get(Number(id));
      if (c) result.push(c);
    }
    return result;
  }

  // No override: all visible columns in base order.
  return dynamic.filter((c) => c.visibility === true);
}

/**
 * Column `field_name` → the query param `ProjectFilter` actually declares.
 *
 * `ksdm_master_project_column.field_name` stores FK columns with an `_id`
 * suffix (`market_id`, `customer_co_id`, `ordering_rep_contact_id`, every
 * `*_co_id`), but `api/rest_project/filters.py` declares those filters WITHOUT
 * it (`market`, `customer_co`, `ordering_rep_contact`). django_filters silently
 * drops params it does not declare, so sending the raw `field_name` returns the
 * whole unfiltered list — the search looks like it did nothing.
 *
 * HeaderView.convertToNormalName (whiteboard/mvc/view/header.js:60-71) did this
 * truncation before building the query hash; the port dropped it. Kept
 * byte-faithful to the original, including `indexOf` (truncate at the FIRST
 * `_id`) rather than a trailing-suffix strip.
 *
 * Only the QUERY PARAM is converted: the sort key and the input's `name`
 * attribute keep the raw `field_name`, exactly as OLD did (`data-field` /
 * `name="{{field_name}}"` in header.tmpl are both unconverted).
 */
export function toFilterParam(fieldName: string): string {
  const index = fieldName.indexOf('_id');
  return index === -1 ? fieldName : fieldName.substr(0, index);
}

/** Sort label for the infobar (name → "Project Name", create_date → "Created on", else column name). */
export function sortLabel(
  sortBy: string | null,
  isManual: boolean,
  columns: IDashboardColumn[] | undefined
): string {
  if (isManual || !sortBy) return 'User Custom Sorted';
  if (sortBy === 'name') return 'Sorted by Project Name';
  if (sortBy === 'create_date' || sortBy === 'created_on_date') return 'Sorted by Created on';
  const col = columns?.find((c) => c.field_name === sortBy);
  // Non-column sort fields (e.g. '-id', which the backend itself writes as a
  // fallback) render as "User Custom Sorted" in OLD — never "Sorted by id".
  return col ? `Sorted by ${col.name}` : 'User Custom Sorted';
}

/** Money/value columns are right-aligned (§3.E / parity 33). */
export const MONEY_FIELDS = new Set([
  'mfg_cost_actual',
  'mfg_price_actual',
  'sell_to_customer',
  'total_rep_commission',
]);
